import {
  Box,
  Alert,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import { useState } from 'react';
import SkuCreateDialog from '../SkuCreateDialog';
import SpecTemplatesDialog from '../SpecTemplatesDialog';
import ItemStatsDrawer from '../ItemStatsDrawer';
import { api } from '../api';
import {
  unitPrice,
  CountTabs,
  Empty,
  Heading,
  ListToolbar,
  Props,
  plain,
  PRICE_DECIMALS,
  Tag,
  type Tone,
  usePagination,
  FitTableContainer,
} from '../ui';
import { can, skuUsageLabels, type SkuUsage } from '@zilian/contracts';

const skuUsageShort: Record<SkuUsage, string> = {
  PURCHASE: '采购物料',
  SALE: '销售产品',
  BOTH: '购销两用',
  INTERNAL: '内部物料',
};

// Bought in blue, our own products in brand teal, both in violet, internal in grey.
const usageTone: Record<SkuUsage, Tone> = {
  PURCHASE: 'info',
  SALE: 'brand',
  BOTH: 'violet',
  INTERNAL: 'neutral',
};

const statsLabel: Record<Exclude<SkuUsage, 'INTERNAL'>, string> = {
  PURCHASE: '采购来源',
  SALE: '销售去向',
  BOTH: '购销去向',
};

/**
 * The quote alone. The material it follows belongs to the item (its spec or 编辑), so it is
 * only stated here. Shared by 商品档案 and 原料行情.
 */
export function editQuote(
  p: Props,
  s: { id: string; name: string; unit: string; salesPrice: any; material?: string | null },
) {
  p.open({
    title: '设置销售报价',
    description: `${s.name} · 每${s.unit}报价。新订单使用此报价，已有订单保留原价。留空表示暂停报价。${
      s.material ? `报价按 ${s.material} 原料行情联动，改价后从当日价格重新起算。` : ''
    }`,
    fields: [
      {
        name: 'salesPrice',
        label: '销售报价（元 / 基础单位）',
        helper: '最多 3 位小数（厘）',
        decimals: PRICE_DECIMALS,
        wide: true,
      },
    ],
    form: { salesPrice: plain(s.salesPrice, PRICE_DECIMALS) },
    submit: (form: any) =>
      api(`/skus/${s.id}/price`, { salesPrice: form.salesPrice === '' ? null : form.salesPrice }),
  });
}

export function Products(p: Props) {
  const [creatingSku, setCreatingSku] = useState(false);
  const [viewing, setViewing] = useState<any>(null);
  const [editing, setEditing] = useState<any>(null);
  const [templates, setTemplates] = useState(false);
  const categories = p.bootstrap.categories ?? [];
  const [usage, setUsage] = useState<SkuUsage | 'ALL'>('ALL');
  // Each tab shows the price its items have: bought ones a purchase price, sold ones a quote,
  // and only to members whose roles see that money.
  const roles: string[] = p.current?.roles ?? [];
  const prices = { purchase: can(roles, 'price.purchase'), sale: can(roles, 'price.sale') };
  const showPurchase = prices.purchase && (usage === 'PURCHASE' || usage === 'BOTH');
  const showSales = prices.sale && usage !== 'PURCHASE';
  const [search, setSearch] = useState('');
  const skus: any[] = p.bootstrap.skus;
  const words = search.trim().toLowerCase();
  const matches = (s: any) =>
    !words ||
    [s.code, s.name, s.spec].some((v) =>
      String(v ?? '')
        .toLowerCase()
        .includes(words),
    );
  // Same pattern as the order lists: fixed tabs with counts that follow the search.
  const count = (u: SkuUsage | 'ALL') =>
    skus.filter((s) => (u === 'ALL' || s.usage === u) && matches(s)).length;
  const shown = skus.filter((s) => (usage === 'ALL' || s.usage === usage) && matches(s));
  const { pageItems, pager } = usePagination(shown, `${usage}|${words}`);
  return (
    <>
      <Heading
        title="商品档案"
        subtitle="管理外购物料与自有产品，分别设置采购、销售用途和基础计量。"
        action={
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={() => setTemplates(true)}>
              规格模板
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreatingSku(true)}>
              新增物料 / 产品
            </Button>
          </Stack>
        }
      />
      <Alert severity="info" sx={{ mb: 3 }}>
        本企业库存档案包括原材料、配件和成品。采购物料不进入销售目录；销售产品用于对外供货，确有转售业务时可设为购销两用。历史档案保留原购销用途，请按实际业务设置。
      </Alert>
      <Paper sx={{ mb: 3 }}>
        <ListToolbar>
          <CountTabs
            value={usage}
            onChange={setUsage}
            items={[
              { value: 'ALL' as const, label: '全部', count: count('ALL') },
              ...(Object.keys(skuUsageShort) as SkuUsage[]).map((u) => ({
                value: u,
                label: skuUsageShort[u],
                count: count(u),
              })),
            ]}
          />
          <TextField
            size="small"
            placeholder="编码、名称或规格"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 230, py: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </ListToolbar>
        {shown.length ? (
          <>
            <FitTableContainer>
              {/* Narrow screens scroll the table instead of breaking headers and prices apart. */}
              {/* One line per item, so the small (compact) MUI row height. */}
              <Table
                size="small"
                stickyHeader
                sx={{ minWidth: 1000, '& th': { whiteSpace: 'nowrap' } }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      '商品编码',
                      '商品名称',
                      '关键规格',
                      '业务用途',
                      '基础单位',
                      ...(showPurchase ? ['参考采购价'] : []),
                      ...(showSales ? ['销售报价'] : []),
                      '操作',
                    ].map((h) => (
                      <TableCell key={h} align={h.endsWith('价') ? 'right' : undefined}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageItems.map((s: any) => (
                    <TableRow key={s.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{s.code}</TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.spec}</TableCell>
                      <TableCell>
                        <Tooltip title={skuUsageLabels[s.usage as SkuUsage]}>
                          <span>
                            <Tag tone={usageTone[s.usage as SkuUsage]}>
                              {skuUsageShort[s.usage as SkuUsage]}
                            </Tag>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{s.unit}</TableCell>
                      {showPurchase && (
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {s.purchasePrice === null ? (
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              —
                            </Box>
                          ) : (
                            unitPrice(s.purchasePrice)
                          )}
                        </TableCell>
                      )}
                      {/* Only the state that needs action gets colour: not-for-sale is a quiet
                        dash, a missing price is amber, a price is plain aligned figures. */}
                      {showSales && (
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {!['SALE', 'BOTH'].includes(s.usage) ? (
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              —
                            </Box>
                          ) : s.salesPrice === null ? (
                            <Box component="span" sx={{ color: '#9a5b06', fontWeight: 600 }}>
                              未报价
                            </Box>
                          ) : (
                            <>
                              {s.material && (
                                <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                                  {s.material}
                                </Box>
                              )}
                              {unitPrice(s.salesPrice)}
                            </>
                          )}
                        </TableCell>
                      )}
                      {/* A small text button has 5px side padding: pull the cell in by that much so
                        the button text lines up with the 操作 header. */}
                      <TableCell sx={{ pl: '15px', whiteSpace: 'nowrap' }}>
                        {/* First, because every traded item has it: the same-width labels keep
                          the buttons in each row lined up. Internal items are not traded. */}
                        {s.usage !== 'INTERNAL' && (
                          <Button size="small" sx={{ mr: 1 }} onClick={() => setViewing(s)}>
                            {statsLabel[s.usage as Exclude<SkuUsage, 'INTERNAL'>]}
                          </Button>
                        )}
                        <Button size="small" onClick={() => setEditing(s)}>
                          编辑
                        </Button>
                        {prices.sale && ['SALE', 'BOTH'].includes(s.usage) && (
                          <Button size="small" sx={{ ml: 1 }} onClick={() => editQuote(p, s)}>
                            设置报价
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </FitTableContainer>
            {pager && <Box sx={{ borderTop: '1px solid #e7edef' }}>{pager}</Box>}
          </>
        ) : (
          <Empty />
        )}
      </Paper>
      {viewing && <ItemStatsDrawer sku={viewing} onClose={() => setViewing(null)} />}
      {templates && (
        <SpecTemplatesDialog
          categories={categories}
          skus={skus}
          onClose={() => setTemplates(false)}
          onSaved={(message) => {
            p.reload();
            p.notify(message);
          }}
        />
      )}
      {editing && (
        <SkuCreateDialog
          defaultUsage={editing.usage}
          categories={categories}
          sku={editing}
          skus={skus}
          recipes={p.bootstrap.recipes}
          prices={prices}
          onClose={() => setEditing(null)}
          onCreated={() => {
            setEditing(null);
            p.reload();
            p.notify('商品档案已保存');
          }}
        />
      )}
      {creatingSku && (
        <SkuCreateDialog
          defaultUsage={usage === 'ALL' ? 'SALE' : usage}
          categories={categories}
          skus={skus}
          recipes={p.bootstrap.recipes}
          prices={prices}
          // Over this dialog, so what is typed here stays.
          onTemplates={() => setTemplates(true)}
          onClose={() => setCreatingSku(false)}
          onContinue={(sku) => {
            p.reload();
            p.notify(`档案已创建，编码 ${sku.code}`);
          }}
          onCreated={(sku) => {
            setCreatingSku(false);
            p.reload();
            p.notify(`档案已创建，编码 ${sku.code}`);
          }}
        />
      )}
    </>
  );
}
